// Registre des sources / références utilisées dans l'app (page Annexes).
// SINGLE SOURCE OF TRUTH, testé : chaque entrée recense une source RÉELLEMENT
// citée dans le code (formule, repère santé, littérature, données), où elle sert,
// et un lien quand il existe. Rien d'inventé : les entrées « limits » recensent
// au contraire les valeurs volontairement NON projetées faute de source.

export type SourceCategory = 'formula' | 'health' | 'sport' | 'data' | 'limits';

export const SOURCE_CATEGORY_LABELS: Record<SourceCategory, string> = {
  formula: 'Formules & méthodes',
  health: 'Repères de santé publique',
  sport: 'Nutrition sportive',
  data: 'Données (aliments)',
  limits: 'Choix assumés & limites (rien d’inventé)',
};

export interface Source {
  id: string;
  title: string;
  /** Référence brute telle qu'attestée dans le code (auteur+année, PMC, autorité). */
  reference: string;
  /** Lien construit à partir de la référence (PMC, autorité) — absent si aucun lien fiable. */
  url?: string;
  /** Où la source est utilisée dans l'app (concret, vérifiable dans le code). */
  usedFor: string[];
  category: SourceCategory;
}

export const SOURCES: Source[] = [
  // --- Formules & méthodes ---
  {
    id: 'mifflin-st-jeor',
    title: 'Métabolisme de base (BMR) — Mifflin-St Jeor',
    reference: 'Mifflin & St Jeor, 1990',
    usedFor: [
      'Estimation du métabolisme de base (domain/energy.ts)',
      'Base du TDEE et des objectifs caloriques auto',
    ],
    category: 'formula',
  },
  {
    id: 'pal-factors',
    title: "Facteurs d'activité (PAL) pour le TDEE",
    reference: 'Multiplicateurs usuels : sédentaire 1.2 → très actif 1.9',
    usedFor: ['Passage BMR → dépense totale (TDEE) selon le niveau d’activité (domain/energy.ts)'],
    category: 'formula',
  },
  {
    id: 'atwater',
    title: "Coefficients d'Atwater (énergie des macronutriments)",
    reference: 'Protéines 4 · Glucides 4 · Lipides 9 · Alcool 7 (kcal/g)',
    usedFor: [
      'Calcul des kcal à partir des macros (domain/energy.ts, macros.ts)',
      'Estimation calorique d’un aliment saisi',
    ],
    category: 'formula',
  },
  {
    id: 'wishnofsky',
    title: 'Équivalence énergie ↔ masse grasse (~7700 kcal/kg)',
    reference: 'Wishnofsky, 1958',
    usedFor: [
      'Déficit → perte théorique en sèche (domain/threshold.ts, reconciliation.ts)',
      'Estimation du TDEE réel observé (domain/tdee-adaptive.ts)',
    ],
    category: 'formula',
  },
  {
    id: 'pearson',
    title: 'Corrélation de Pearson',
    reference: 'Méthode statistique standard',
    usedFor: ['Lien entre bilan énergétique et variation de poids (page Analyse)'],
    category: 'formula',
  },
  // --- Repères de santé publique ---
  {
    id: 'oms-imc',
    title: "Catégories d'IMC de l'adulte",
    reference: 'OMS',
    url: 'https://www.who.int/health-topics/obesity',
    usedFor: ['Classement de l’IMC et zone de poids santé (domain/bmi.ts, page Poids)'],
    category: 'health',
  },
  {
    id: 'oms-sat-fat',
    title: 'Acides gras saturés ≤ 10 % de l’énergie',
    reference: 'OMS',
    url: 'https://www.who.int/publications/i/item/9789240073630',
    usedFor: [
      'Repère « acides gras saturés » (page Macros)',
      'Note sous le réglage lipides (Profil)',
    ],
    category: 'health',
  },
  {
    id: 'oms-free-sugars',
    title: 'Sucres libres < 10 % de l’énergie (idéalement < 5 %)',
    reference: 'OMS',
    url: 'https://www.who.int/publications/i/item/9789241549028',
    usedFor: [
      'Repère « sucres » aligné sur l’OMS = 50 g (10 %E sur 2000 kcal, page Macros)',
      'Réserve : les données sont des sucres TOTAUX, l’OMS vise les sucres LIBRES → borne prudente',
    ],
    category: 'health',
  },
  {
    id: 'oms-salt',
    title: 'Sel < 5 g/jour',
    reference: 'OMS',
    url: 'https://www.who.int/news-room/fact-sheets/detail/salt-reduction',
    usedFor: ['Repère « sel » aligné sur l’OMS (page Macros)'],
    category: 'health',
  },
  {
    id: 'efsa-iom-macros',
    title: 'Répartition énergétique : lipides 20–35 %, glucides 45–60 %',
    reference: 'EFSA / IOM (DRV)',
    url: 'https://www.efsa.europa.eu/en/topics/topic/dietary-reference-values',
    // La bande lipides est la seule réellement affichée à partir de cette source
    // (réglage Profil). La bande protéines vient de l'ISSN (voir ci-dessous).
    usedFor: ['Bande lipides 20–35 % sous le réglage des macros (Profil)'],
    category: 'health',
  },
  {
    id: 'efsa-water',
    title: "Valeurs de référence pour l'eau",
    reference: 'EFSA, 2010',
    url: 'https://www.efsa.europa.eu/en/efsajournal/pub/1459',
    usedFor: [
      'Rappel hydratation après une séance (qualitatif)',
      'Cadre des repères d’hydratation (domain/health-guidelines.ts)',
    ],
    category: 'health',
  },
  {
    id: 'anses-fibres',
    // Pas d'URL : la page ANSES « vitamines et minéraux » ne couvre pas les fibres,
    // et mieux vaut aucun lien qu'un lien qui n'atteste pas l'affirmation (règle n°1).
    title: 'Fibres ~30 g/jour',
    reference: 'ANSES (références nutritionnelles glucides/fibres)',
    usedFor: ['Repère « fibres » (page Macros)'],
    category: 'health',
  },
  // --- Nutrition sportive ---
  {
    id: 'issn-2017',
    title: 'Apports en protéines selon la phase (g/kg/jour)',
    reference: 'ISSN Position Stand, 2017 (PMC5477153)',
    url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5477153/',
    usedFor: [
      'Bandes de protéines par phase — sèche 2.3–3.1, général 1.4–2.0 (domain/health-guidelines.ts)',
      'Repère g/kg affiché sur la page Macros et le Profil',
    ],
    category: 'sport',
  },
  {
    id: 'hypertrophy-surplus',
    title: 'Surplus calorique en prise de masse (~+300–500 kcal/j)',
    reference: 'Revue hypertrophie (PMC6710320)',
    url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6710320/',
    usedFor: ['Bande de surplus recommandée en prise de masse (réglage Profil)'],
    category: 'sport',
  },
  {
    id: 'garthe-2013',
    title: 'Un surplus trop grand augmente surtout la masse grasse',
    reference: 'Garthe et al., 2013',
    usedFor: ['Justification d’un surplus conservateur (domain/health-guidelines.ts)'],
    category: 'sport',
  },
  {
    id: 'tipton-2015',
    title: 'Récupération = équilibre énergétique (maintenance)',
    reference: 'Tipton, 2015',
    usedFor: [
      'Seuil calorique = maintenance en récupération (domain/threshold.ts)',
      'Note sous le sélecteur de phase (Profil)',
    ],
    category: 'sport',
  },
  // --- Données ---
  {
    id: 'ciqual-usda',
    title: "Composition nutritionnelle du catalogue d'aliments",
    reference: 'Ciqual (ANSES) / USDA FoodData Central',
    url: 'https://ciqual.anses.fr/',
    usedFor: ['Valeurs /100 g des aliments pré-remplis (catalogue public seedé)'],
    category: 'data',
  },
  // --- Choix assumés & limites (rien d'inventé) ---
  {
    id: 'protein-surplus-link',
    title: 'Prise de masse : surplus ET protéines (pourquoi on mesure le surplus en kcal)',
    reference:
      'Le surplus se mesure en kcal (énergie disponible pour construire), mais il se remplit surtout de glucides/lipides ; les protéines suffisantes (bande ISSN) restent nécessaires pour que le surplus serve à la masse maigre. Les deux comptent — on règle l’un en kcal, l’autre en g/kg.',
    usedFor: [
      'Réglage du surplus (kcal) et de la cible protéique (g/kg) séparément dans le Profil',
    ],
    category: 'limits',
  },
  {
    id: 'recovery-band',
    title: 'Bande de tolérance en récupération : ±10 % du TDEE (choix UX assumé)',
    reference:
      'Aucune source physiologique ne fixe une largeur de bande « proche de la maintenance ». ±10 % est un choix d’affichage assumé (s’adapte au gabarit), pas une valeur sourcée.',
    usedFor: ['Couleur « dans la cible » vs « hors cible » en récupération (calories, calendrier)'],
    category: 'limits',
  },
  {
    id: 'no-kg-gain-projection',
    title: 'Pas de projection de gain en kg en prise de masse',
    reference:
      'Le muscle n’est pas 7700 kcal/kg : projeter des kg à partir d’un surplus serait faux. La courbe « théorique » n’est tracée qu’en sèche ; le surplus réglable est borné à +1000 kcal/j.',
    usedFor: [
      'Page Analyse (courbe masquée hors sèche)',
      'Plafond de surplus (domain/threshold.ts)',
    ],
    category: 'limits',
  },
  {
    id: 'no-water-formula',
    title: 'Pas de formule « kcal brûlées → ml d’eau »',
    reference:
      'Aucune source publique ne donne une telle formule. On affirme seulement le fait sourcé (EFSA) que les besoins en eau augmentent avec l’activité — rappel qualitatif, sans chiffre.',
    usedFor: ['Rappel d’hydratation après une séance de sport'],
    category: 'limits',
  },
  {
    id: 'water-per-kg-empirical',
    title: 'Hydratation ~30–40 ml/kg/jour : règle empirique, pas OMS/EFSA',
    reference:
      'Repère clinique empirique. L’EFSA exprime l’apport en volume absolu (~2,0 L/j F, 2,5 L/j H), pas au poids. Affiché comme tel.',
    usedFor: ['Objectif d’hydratation calculé au poids (réglage Hydratation)'],
    category: 'limits',
  },
];

/** Regroupe les sources par catégorie, dans l'ordre d'affichage. */
export function sourcesByCategory(): Record<SourceCategory, Source[]> {
  const order: SourceCategory[] = ['formula', 'health', 'sport', 'data', 'limits'];
  const out = {} as Record<SourceCategory, Source[]>;
  for (const cat of order) out[cat] = SOURCES.filter((s) => s.category === cat);
  return out;
}
